import {ButtonConfig, ButtonStyle, ImagePdfViewerConfig, Point} from './imagepdfviewer.config';
import {Subject} from 'rxjs';

export interface UserDefinedButton {
  polygon: Array<Point>;
  button: Button;
}

export class Button {
  //#region Properties
  sortId = 0;

  icon: string;
  tooltip: string;

  // hover state
  hover: boolean | (() => boolean) = false;

  // show/hide button
  display = true;

  // drawn on position
  private drawPosition: any = null;
  private drawRadius = 0;

  private path: Path2D = null;
  // private polygonAccountedForRenderOnFile: Array<Point> = null;
  //#endregion

  //#region Lifecycle events
  constructor(
    config: ButtonConfig,
    private style: ButtonStyle
  ) {
    this.sortId = config?.sortId;
    this.display = config?.show;
    this.icon = config?.icon;
    this.tooltip = config?.tooltip;
  }
  //#endregion

  //#region Events
  // click action
  onClick(evt: any) { alert('no click action set!'); return true; }

  // mouse down action
  onMouseDown(evt: any) { return false; }
  //#endregion

  //#region Draw Button
  // @ts-ignore
  drawAsCircle(ctx, x, y, radius) {
    this.drawPosition = { x: x, y: y };
    this.drawRadius = radius;

    // preserve context
    ctx.save();

    // drawing settings
    const isHover = (typeof this.hover === 'function') ? this.hover() : this.hover;
    ctx.globalAlpha = (isHover) ? this.style.hoverAlpha : this.style.alpha;
    ctx.fillStyle = this.style.bgStyle;
    ctx.lineWidth = 0;

    // draw circle
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.fill();
    if (this.style.borderWidth > 0) {
      ctx.lineWidth = this.style.borderWidth;
      ctx.strokeStyle = this.style.borderStyle;
      ctx.stroke();
    }

    // draw icon
    if (this.icon !== null && this.icon !== undefined) {
      ctx.save();
      // ctx.globalCompositeOperation = 'destination-out';
      this.drawIconFont(ctx, x, y, radius);
      ctx.restore();
    }

    // restore context
    ctx.restore();
  }

  drawAsPolygon(ctx: any, polygon: Array<Point>) {
    // preserve context
    ctx.save();

    // drawing settings
    const isHover = (typeof this.hover === 'function') ? this.hover() : this.hover;
    ctx.globalAlpha = (isHover) ? this.style.hoverAlpha : this.style.alpha;
    ctx.fillStyle = this.style.bgStyle;
    ctx.lineWidth = 0;

    // draw polygon
    const path: Path2D = new Path2D();
    polygon.forEach((point, index) => {
      if (index === 0) {
        path.moveTo(point.x, point.y);
      } else {
        path.lineTo(point.x, point.y);
      }
    });
    path.closePath();
    ctx.fill(path);
    if (this.style.borderWidth > 0) {
      ctx.lineWidth = this.style.borderWidth;
      ctx.strokeStyle = this.style.borderStyle;
      ctx.stroke(path);
    }
    this.path = path;

    // restore context
    ctx.restore();
  }

  // @ts-ignore
  private drawIconFont(ctx, centreX, centreY, size) {
    // font settings
    ctx.font = size + 'px ' + this.style.iconFontFamily;
    ctx.fillStyle = this.style.iconStyle;

    // calculate position
    const textSize = ctx.measureText(this.icon);
    const x = centreX - textSize.width / 2;
    const y = centreY + size / 2;

    // draw it
    ctx.fillText(this.icon, x, y);
  }
  //#endregion

  //#region Utils
  // @ts-ignore
  isWithinBounds(ctx, x, y) {
    if (this.path !== null) { // If button is a polygon
      return ctx.isPointInPath(this.path, x, y);
    }

    if (this.drawPosition !== null) { // If button is a circle
      const dx = Math.abs(this.drawPosition.x - x), dy = Math.abs(this.drawPosition.y - y);
      return dx * dx + dy * dy <= this.drawRadius * this.drawRadius;
    }

    return false;
  }
  //#endregion
}

export class Viewport {
  constructor(
    public width: number,
    public height: number,
    public scale: number,
    public rotation: number,
    public x: number,
    public y: number
  ) {}
}

export interface Dimension { width: number; height: number; }

export abstract class ResourceLoader {
  public src: string;
  public sourceDim: { width: number, height: number };
  public viewport: Viewport = { width: 0, height: 0, scale: 1, rotation: 0, x: 0, y: 0 };
  public minScale = 0;
  public maxScale = 4;
  public currentItem = 1;
  public totalItem = 1;
  public showItemsQuantity = false;
  public loaded = false;
  public loading = false;
  public rendering = false;

  protected _image: CanvasImageSource;
  protected resourceChange = new Subject<string>();

  // @ts-ignore
  abstract setUp();
  // @ts-ignore
  abstract loadResource();

  // @ts-ignore
  public resetViewport(canvasDim: Dimension): boolean {
    if (!this.loaded || !canvasDim) { // @ts-ignore
      return; }

    this.viewport.rotation = 0;
    const rotation = this.viewport ? this.viewport.rotation : 0;
    const inverted = toSquareAngle(rotation) / 90 % 2 !== 0;
    const canvas = {
      width: !inverted ? canvasDim.width : canvasDim.height,
      height: !inverted ? canvasDim.height : canvasDim.width
    };

    // @ts-ignore
    if (((canvas.height / this._image.height) * this._image.width) <= canvas.width) {
      // @ts-ignore
      this.viewport.scale = canvas.height / this._image.height;
    } else {
      // @ts-ignore
      this.viewport.scale = canvas.width / this._image.width;
    }
    this.minScale = this.viewport.scale / 4;
    this.maxScale = this.viewport.scale * 4;

    // start point to draw image
    // @ts-ignore
    this.viewport.width = this._image.width * this.viewport.scale;
    // @ts-ignore
    this.viewport.height = this._image.height * this.viewport.scale;
    this.viewport.x = (canvasDim.width - this.viewport.width) / 2;
    this.viewport.y = (canvasDim.height - this.viewport.height) / 2;
  }


  public draw(
    ctx: CanvasRenderingContext2D,
    config: ImagePdfViewerConfig,
    canvasDim: Dimension,
    drawCallbacks: Array<(ctx: CanvasRenderingContext2D) => void>,
    userDefinedButtons: Array<UserDefinedButton>,
    onFinish: (ctx: any, config: any, canvasDim: any) => void
  ) {
    // clear canvas
    ctx.clearRect(0, 0, canvasDim.width, canvasDim.height);

    // Draw background color;
    ctx.fillStyle = config.bgStyle;
    ctx.fillRect(0, 0, canvasDim.width, canvasDim.height);

    // draw image (transformed, rotate and scaled)
    if (!this.loading && this.loaded) {
      ctx.save();
      ctx.translate(this.viewport.x + this.viewport.width / 2, this.viewport.y + this.viewport.height / 2);
      ctx.rotate(this.viewport.rotation * Math.PI / 180);
      ctx.scale(this.viewport.scale, this.viewport.scale);
      ctx.translate(-this._image.width / 2, -this._image.height / 2);
      ctx.drawImage(this._image, 0, 0);

      // execute user defined draw callbacks
      if (drawCallbacks) {
        drawCallbacks.forEach((cb) => {
          cb(ctx);
        });
      }
      // draw user defined buttons on file
      if (userDefinedButtons) {
        ctx.restore();
        userDefinedButtons.forEach((btnObject) => {
          btnObject.button.drawAsPolygon(
            ctx,
            this.accountForViewPort(btnObject.polygon)
          );
        });
      }
    } else {
      ctx.fillStyle = '#333';
      ctx.font = '25px Verdana';
      ctx.textAlign = 'center';
      ctx.fillText(config.loadingMessage || 'Loading...', canvasDim.width / 2, canvasDim.height / 2);
    }
    onFinish(ctx, config, canvasDim);
  }

  public onResourceChange() { return this.resourceChange.asObservable(); }

  accountForViewPort(polygon: Array<Point>): Array<Point> {
    const accountedForZoom: Array<Point> = this.accountForZoom(polygon);

    const accountedForRotationAndZoom: Array<Point> = this.accountForRotation(accountedForZoom);

    return accountedForRotationAndZoom;
  }

  private accountForZoom(polygon: Array<Point>): Array<Point> {
    return polygon.map((point, i) => {
      const x = point.x * this.viewport.scale;
      const y = point.y * this.viewport.scale;

      // @ts-ignore
      const xMove = (this.viewport.x + this.viewport.width / 2) - (this._image.width / 2 * this.viewport.scale);
      // @ts-ignore
      const yMove = (this.viewport.y + this.viewport.height / 2) - (this._image.height / 2 * this.viewport.scale);

      return {
        x: x + xMove,
        y: y + yMove
      };
    });
  }

  private accountForRotation(polygon: Array<Point>): Array<Point> {
    const angle = this.viewport.rotation * Math.PI / 180;
    return this.rotatePolygon(polygon, angle);
  }

  getPolygonAsPositionOnImage(polygon: Array<Point>) {
    const polygonRotatedBackToOrigin: Array<Point> = this.getPolygonRotatedBackToOrigin(polygon);

    const polygonRotatedBackToOriginAndNoZoom: Array<Point> = this.getPolygonWithNoZoom(polygonRotatedBackToOrigin);

    return polygonRotatedBackToOriginAndNoZoom;
  }

  private getPolygonWithNoZoom(polygon: Array<Point>): Array<Point> {
    return polygon.map((point, i) => {
      // @ts-ignore
      const xMove = (this.viewport.x + this.viewport.width / 2) - (this._image.width / 2 * this.viewport.scale);
      // @ts-ignore
      const yMove = (this.viewport.y + this.viewport.height / 2) - (this._image.height / 2 * this.viewport.scale);

      return {
        x: (point.x - xMove) / this.viewport.scale,
        y: (point.y - yMove) / this.viewport.scale
      };
    });
  }

  private getPolygonRotatedBackToOrigin(polygon: Array<Point>): Array<Point> {
    const angle = -this.viewport.rotation * Math.PI / 180;
    return this.rotatePolygon(polygon, angle);
  }

  rotatePolygon(polygon: Array<Point>, angle: number): Array<Point> {
    // rotate around center of canvas
    const rotationPoint: Point = {
      x: this.viewport.x + this.viewport.width / 2,
      y: this.viewport.y + this.viewport.height / 2
    };
    return polygon.map((point) => {
      const xRotated = rotationPoint.x + Math.cos(angle) * (point.x - rotationPoint.x) - Math.sin(angle) * (point.y - rotationPoint.y);
      const yRotated = rotationPoint.y + Math.sin(angle) * (point.x - rotationPoint.x) + Math.cos(angle) * (point.y - rotationPoint.y);
      return {
        x: xRotated,
        y: yRotated
      };
    });
  }
}

export function toSquareAngle(angle: number) {
  return 90 * ((Math.trunc(angle / 90) + (Math.trunc(angle % 90) > 45 ? 1 : 0)) % 4);
}
